import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { getRoleLabel, getRoleBadgeClass } from '@/lib/utils/utils';
import { useTheme } from '@/lib/contexts/theme-context';
import { useAuth } from '@/lib/contexts/auth-context';
import {
    Bell,
    ChevronRight,
    LogOut,
    AlertCircle,
    Loader2,
    Send,
    HelpCircle,
    Camera,
    Save,
    Star,
    MapPin,
} from 'lucide-react';

export default function AccountTabContent({
    // User data
    user,
    firstName,
    lastName,
    email,
    companyName,
    isEmailVerified,
    isLoadingAccount,
    accountError,
    settingsError,
    // Verification
    isSendingVerification,
    handleSendVerificationEmail,
    // Sheet openers
    setBillingEditOpen,
    setEmailEditOpen,
    setPasswordEditOpen,
    // Notifications
    emailPromotions,
    emailUpdates,
    // Theme is accessed via hook below
    mobilePromotions,
    mobileUpdates,
    setEmailPromotions,
    setEmailUpdates,
    setMobilePromotions,
    setMobileUpdates,
    computeMailsFlag,
    saveSettings,
    isSavingSettings,
    // Location quality
    locationQuality,
    setLocationQuality,
    locationOptions,
    LOCATION_QUALITY_STORAGE_KEY,
    hideLocationQuality = false,
    // Photo quality
    photoQuality,
    setPhotoQuality,
    photoOptions,
    uiPhotoQualityToApi,
    // Auto save
    autoSave,
    setAutoSave,
    // Toast
    toast,
}) {
    const { t } = useTranslation('account');
    const { theme } = useTheme();
    const { logout } = useAuth();
    const { storeLinks } = theme;
    const hasStoreLinks = storeLinks?.appStore || storeLinks?.googlePlay;

    const QualitySelector = ({ options, value, onChange }) => (
        <div className="flex gap-2.5">
            {options.map((option) => (
                <Button
                    key={option.value}
                    variant="solid"
                    color="neutral"
                    size="xmd"
                    onClick={() => onChange(option.value)}
                    className={`flex-1 flex flex-col items-center gap-0 ${
                        value === option.value
                            ? 'bg-secondary text-secondary-foreground hover:bg-secondary/90'
                            : 'bg-surface text-muted-foreground hover:bg-surface-hover'
                    }`}
                >
                    <p className="text-[13px] font-medium">{option.label}</p>
                    {option.subtitle && <p className="text-[10px] opacity-70">{option.subtitle}</p>}
                </Button>
            ))}
        </div>
    );

    return (
        <div className="px-8 space-y-6">
            {/* Email Verification Banner - affiché seulement si user chargé et email non vérifié */}
            {user && !isEmailVerified && (
                <div className="bg-warning/10 border border-warning/30 rounded-xl p-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-warning flex-shrink-0" />
                        <div>
                            <p className="font-medium text-warning">{t('emailNotVerified.title')}</p>
                            <p className="text-sm text-warning/80">{t('emailNotVerified.description')}</p>
                        </div>
                    </div>
                    <Button
                        variant="solid"
                        color="warning"
                        size="xmd"
                        onClick={handleSendVerificationEmail}
                        disabled={isSendingVerification}
                        className="flex items-center gap-2 px-4 py-2 disabled:bg-warning/50 text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
                    >
                        {isSendingVerification ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                {t('sendingEllipsis')}
                            </>
                        ) : (
                            <>
                                <Send className="w-4 h-4" />
                                {t('resendEmail')}
                            </>
                        )}
                    </Button>
                </div>
            )}

            {/* Two columns layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Column 1: Profile + Centre d'aide */}
                <div className="space-y-6">
                    {/* Profile Info + Actions */}
                    <div className="bg-card rounded-2xl overflow-hidden border border-border">
                        {/* {accountError && <div className="text-sm font-semibold text-error p-4">{accountError}</div>} */}
                        {settingsError && <div className="text-sm font-semibold text-error p-4">{settingsError}</div>}

                        {/* Profile Header with gradient */}
                        <div className="bg-gradient-to-br from-secondary/10 to-secondary/5 w-full p-6 flex flex-col items-center border-b border-border">
                            <div className="bg-secondary rounded-full h-15 w-15 flex items-center justify-center mb-3">
                                {user?.photo ? (
                                    <img
                                        src={user.photo}
                                        alt={t('profilePhotoAlt')}
                                        className="w-full h-full rounded-full object-cover"
                                    />
                                ) : isLoadingAccount ? (
                                    <Skeleton className="w-full h-full rounded-full" />
                                ) : (
                                    <span className="text-2xl text-secondary-foreground font-semibold">
                                        {`${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()}
                                    </span>
                                )}
                            </div>
                            <h2 className="text-xl text-foreground mb-1">
                                {isLoadingAccount ? (
                                    <Skeleton className="h-6 w-40" />
                                ) : (
                                    `${firstName} ${lastName}`.trim()
                                )}
                            </h2>
                            <p className="text-sm text-muted-foreground">
                                {isLoadingAccount ? <Skeleton className="h-4 w-56" /> : email}
                            </p>
                            {user?.role && (
                                <div className={`px-3 py-1 rounded-md mt-2 ${getRoleBadgeClass(user.role)}`}>
                                    <p className="text-[13px]">{getRoleLabel(user.role)}</p>
                                </div>
                            )}
                            {companyName && (
                                <div className="bg-secondary/20 px-3 py-1 rounded-md mt-2">
                                    <p className="text-sm text-foreground">
                                        {isLoadingAccount ? <Skeleton className="h-4 w-32" /> : companyName}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <Button
                            variant="plain"
                            color="neutral"
                            size="xxl"
                            className="w-full p-5 h-auto flex items-center justify-between rounded-none border-b border-border whitespace-normal"
                            onClick={() => setBillingEditOpen(true)}
                        >
                            <span className="text-sm text-start text-foreground">{t('editBilling')}</span>
                            <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        </Button>
                        <Button
                            variant="plain"
                            color="neutral"
                            size="xxl"
                            className="w-full p-5 h-auto flex items-center justify-between rounded-none border-b border-border"
                            onClick={() => setEmailEditOpen(true)}
                        >
                            <span className="text-sm text-start text-foreground">{t('editEmail')}</span>
                            <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        </Button>
                        <Button
                            variant="plain"
                            color="neutral"
                            size="xxl"
                            className="w-full p-5 h-auto flex items-center justify-between rounded-none"
                            onClick={() => setPasswordEditOpen(true)}
                        >
                            <span className="text-sm text-start text-foreground">{t('editPassword')}</span>
                            <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        </Button>
                    </div>

                    {/* Centre d'aide */}
                    <div className="bg-card rounded-2xl overflow-hidden border border-border">
                        <Button
                            variant="plain"
                            color="neutral"
                            size="xxl"
                            className="w-full p-5 flex items-center gap-[15px] hover:bg-surface-hover transition-colors rounded-none h-auto justify-start"
                            asChild
                        >
                            <Link to="/help">
                                <div className="bg-surface rounded-full h-10 w-10 flex items-center justify-center shrink-0">
                                    <HelpCircle className="h-5 w-5 text-foreground" />
                                </div>
                                <div className="flex-1 text-left">
                                    <p className="text-sm text-foreground">{t('helpCenter')}</p>
                                </div>
                                <ChevronRight className="h-5 w-5 text-muted-foreground" />
                            </Link>
                        </Button>
                    </div>
                </div>

                {/* Column 2: Notifications + Quality Settings (single card) */}
                <div className="bg-card rounded-2xl overflow-hidden border border-border">
                    {/* Notifications Section */}
                    <div className="p-6 border-b border-border">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 bg-surface rounded-full flex items-center justify-center">
                                <Bell className="w-5 h-5 text-muted-foreground" />
                            </div>
                            <h3 className="text-base font-semibold text-foreground">{t('notifications.title')}</h3>
                        </div>

                        <div className="space-y-4">
                            {/* Email Notifications */}
                            <div className="flex items-center justify-between">
                                <div>
                                    <h4 className="text-sm font-medium text-foreground">{t('notifications.email')}</h4>
                                    <p className="text-xs text-muted-foreground">{t('notifications.subtitle')}</p>
                                </div>
                                <Switch
                                    checked={emailPromotions || emailUpdates}
                                    onCheckedChange={async (next) => {
                                        const prev = { emailPromotions, emailUpdates };
                                        setEmailPromotions(next);
                                        setEmailUpdates(next);
                                        try {
                                            const mails = computeMailsFlag({
                                                emailPromotions: next,
                                                emailUpdates: next,
                                                mobilePromotions,
                                                mobileUpdates,
                                            });
                                            await saveSettings({ mails });
                                        } catch {
                                            setEmailPromotions(prev.emailPromotions);
                                            setEmailUpdates(prev.emailUpdates);
                                        }
                                    }}
                                    disabled={isSavingSettings || isLoadingAccount}
                                />
                            </div>

                            {/* Mobile Notifications */}
                            <div className="flex items-center justify-between pt-4 border-t border-border">
                                <div>
                                    <h4 className="text-sm font-medium text-foreground">{t('notifications.mobile')}</h4>
                                    <p className="text-xs text-muted-foreground">{t('notifications.subtitle')}</p>
                                </div>
                                <Switch
                                    checked={mobilePromotions || mobileUpdates}
                                    onCheckedChange={async (next) => {
                                        const prev = { mobilePromotions, mobileUpdates };
                                        setMobilePromotions(next);
                                        setMobileUpdates(next);
                                        try {
                                            const mails = computeMailsFlag({
                                                emailPromotions,
                                                emailUpdates,
                                                mobilePromotions: next,
                                                mobileUpdates: next,
                                            });
                                            await saveSettings({ mails });
                                        } catch {
                                            setMobilePromotions(prev.mobilePromotions);
                                            setMobileUpdates(prev.mobileUpdates);
                                        }
                                    }}
                                    disabled={isSavingSettings || isLoadingAccount}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Qualité de géolocalisation Section */}
                    {!hideLocationQuality && (
                        <div className="p-6 border-b border-border">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 bg-surface rounded-full flex items-center justify-center">
                                    <MapPin className="w-5 h-5 text-muted-foreground" />
                                </div>
                                <h3 className="text-base font-semibold text-foreground">
                                    {t('geolocationQuality.title')}
                                </h3>
                            </div>
                            <QualitySelector
                                options={locationOptions}
                                value={locationQuality}
                                onChange={(next) => {
                                    setLocationQuality(next);
                                    localStorage.setItem(LOCATION_QUALITY_STORAGE_KEY, next);
                                    toast.success(t('geolocationQuality.savedToast'));
                                }}
                            />
                        </div>
                    )}

                    {/* Qualité des photos Section */}
                    <div className="p-6 border-b border-border">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-surface rounded-full flex items-center justify-center">
                                <Camera className="w-5 h-5 text-muted-foreground" />
                            </div>
                            <h3 className="text-base font-semibold text-foreground">{t('photoQuality.title')}</h3>
                        </div>
                        <QualitySelector
                            options={photoOptions}
                            value={photoQuality}
                            onChange={async (next) => {
                                const prev = photoQuality;
                                setPhotoQuality(next);
                                try {
                                    await saveSettings({ quality: uiPhotoQualityToApi(next) });
                                } catch {
                                    setPhotoQuality(prev);
                                }
                            }}
                        />
                    </div>

                    {/* Sauvegarde automatique Section */}
                    <div className="p-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-surface rounded-full flex items-center justify-center">
                                    <Save className="w-5 h-5 text-muted-foreground" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-semibold text-foreground">{t('autoSave.title')}</h4>
                                    <p className="text-xs text-muted-foreground">{t('autoSave.description')}</p>
                                </div>
                            </div>
                            <Switch
                                checked={autoSave}
                                onCheckedChange={async (next) => {
                                    const prev = autoSave;
                                    setAutoSave(next);
                                    try {
                                        await saveSettings({ download_auto: next ? 1 : 0 });
                                    } catch {
                                        setAutoSave(prev);
                                    }
                                }}
                                disabled={isSavingSettings || isLoadingAccount}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Rating Section - Full width below grid */}
            <div className="hidden bg-secondary-foreground text-primary-foreground rounded-2xl p-8 text-center space-y-4">
                <div className="flex items-center justify-center gap-2 text-2xl">
                    <Star className="w-6 h-6 fill-secondary text-primary" />
                    <Star className="w-6 h-6 fill-secondary text-primary" />
                    <Star className="w-6 h-6 fill-secondary text-primary" />
                    <Star className="w-6 h-6 fill-secondary text-primary" />
                    <Star className="w-6 h-6 fill-secondary text-primary" />
                </div>
                <h3 className="text-xl font-bold">{t('rating.title', { themeName: theme.name })}</h3>
                <p className="text-sm opacity-90">{t('rating.description')}</p>
                {hasStoreLinks && (
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        {storeLinks.appStore && (
                            <Button
                                variant="outline"
                                color="neutral"
                                asChild
                                className="bg-card text-foreground hover:bg-card/90"
                            >
                                <Link to={storeLinks.appStore} target="_blank" rel="noopener noreferrer">
                                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                                    </svg>
                                    {t('rating.appStore')}
                                </Link>
                            </Button>
                        )}
                        {storeLinks.googlePlay && (
                            <Button
                                variant="outline"
                                color="neutral"
                                asChild
                                className="bg-card text-foreground hover:bg-card/90"
                            >
                                <Link to={storeLinks.googlePlay} target="_blank" rel="noopener noreferrer">
                                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M3 20.5v-17c0-.59.34-1.11.84-1.35L13.69 12l-9.85 9.85c-.5-.24-.84-.76-.84-1.35zm13.81-5.38L6.05 21.34l8.49-8.49 2.27 2.27zm3.35-4.31c.34.27.59.69.59 1.19s-.22.9-.57 1.18l-2.29 1.32-2.5-2.5 2.5-2.5 2.27 1.31zM6.05 2.66l10.76 6.22-2.27 2.27-8.49-8.49z" />
                                    </svg>
                                    {t('rating.googlePlay')}
                                </Link>
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {/* Logout Button - Full width below grid */}
            <Button
                variant="plain"
                color="neutral"
                size="xxl"
                className="bg-surface-active hover:bg-muted rounded-2xl w-full p-5 h-auto flex items-center justify-center gap-4 transition-colors mb-20"
                onClick={logout}
            >
                <LogOut className="w-5 h-5 text-muted-foreground" />
                <span className="text-base text-muted-foreground">{t('logout')}</span>
            </Button>
        </div>
    );
}
